import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker, type BlockerFunction } from 'react-router-dom'
import { fieldLabel, sentenceList } from '@/lib/format'
import { Dialog } from './ui/dialog'
import { Button } from './ui/primitives'

export interface LeaveGuardState {
  open: boolean
  fields: readonly string[]
  /** Cancel the departure and keep the edits. */
  stay: () => void
  /** Throw the edits away and go. */
  leave: () => void
  /**
   * For a control that leaves a screen without navigating - the Product and Country
   * pages sit inside their listing and go back by clearing a piece of state, which no
   * router hook can see. Runs the action straight through when there is nothing to lose.
   */
  confirm: (go: () => void) => void
  /**
   * Leave without asking, for a departure the page has already decided is safe - the
   * record being edited has just been deleted, so there is nothing for the edits to be
   * saved to and nothing worth asking about.
   */
  bypass: (go: () => void) => void
}

/**
 * Stops a half-edited form being abandoned by accident.
 *
 * Three ways out of a page, three answers:
 * a link or a Back button goes through the router and is caught by `useBlocker`; closing
 * the tab or reloading is the browser's own prompt, which is the most any page is allowed
 * to do; and a screen that goes back by changing state rather than navigating calls
 * `confirm` itself.
 *
 * `useBlocker` needs a data router, which is why `main.tsx` mounts one.
 */
export function useLeaveGuard(changes: { isDirty: boolean; fields: readonly string[] }): LeaveGuardState {
  const { isDirty, fields } = changes
  const [pending, setPending] = useState<(() => void) | undefined>(undefined)

  // A ref rather than state because bypass() has to take effect inside the same call
  // stack as the navigation it is releasing, before React has a chance to re-render.
  const releasing = useRef(false)

  // Only a move to another page counts. A search or a hash on the same page is the
  // screen talking to itself, and prompting about it would fire on its own controls.
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      !releasing.current && isDirty && currentLocation.pathname !== nextLocation.pathname,
    [isDirty],
  )

  const blocker = useBlocker(shouldBlock)

  useEffect(() => {
    if (!isDirty) return

    // preventDefault is the whole of the modern API: the browser writes the wording, and
    // a page that supplied its own would be ignored anyway.
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [isDirty])

  // Saving or discarding while the prompt is up answers the question it was asking, so
  // the block is released rather than left holding a navigation nobody is deciding on.
  useEffect(() => {
    if (isDirty) return

    if (blocker.state === 'blocked') blocker.proceed()
    setPending(undefined)
  }, [isDirty, blocker])

  const stay = useCallback(() => {
    if (blocker.state === 'blocked') blocker.reset()
    setPending(undefined)
  }, [blocker])

  const leave = useCallback(() => {
    if (pending) {
      const go = pending
      setPending(undefined)
      go()
      return
    }

    if (blocker.state === 'blocked') blocker.proceed()
  }, [blocker, pending])

  const confirm = useCallback(
    (go: () => void) => {
      if (!isDirty) {
        go()
        return
      }

      setPending(() => go)
    },
    [isDirty],
  )

  const bypass = useCallback((go: () => void) => {
    releasing.current = true
    go()
  }, [])

  return {
    open: blocker.state === 'blocked' || pending !== undefined,
    fields,
    stay,
    leave,
    confirm,
    bypass,
  }
}

/**
 * The prompt itself.
 *
 * It names the fields. "You have unsaved changes" makes somebody who has been editing for
 * ten minutes guess what they are about to lose. Staying is the safe answer, so it is
 * first in the footer - which is also what the dialog focuses when it opens.
 */
export function LeaveGuard({ guard }: { guard: LeaveGuardState }) {
  const named = sentenceList(guard.fields.map(fieldLabel))

  return (
    <Dialog
      open={guard.open}
      title="Unsaved changes"
      // Closing this dialog by any of the ways a dialog closes means the question was
      // not answered, and the safe answer to an unanswered question is to stay.
      onClose={guard.stay}
      footer={
        <>
          <Button size="sm" onClick={guard.stay}>
            Stay on this page
          </Button>
          <Button size="sm" variant="danger" onClick={guard.leave}>
            Discard and leave
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2">
        {guard.fields.length === 1
          ? `${named} has been edited and not saved.`
          : `${named} have been edited and not saved.`}{' '}
        Leaving now discards {guard.fields.length === 1 ? 'it' : 'them'}.
      </p>
    </Dialog>
  )
}
